/* eslint-disable arrow-body-style */
import React, { useContext, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    TableFooter,
    Pagination,
    Grid,
    Tooltip,
    IconButton,
    Typography
} from '@mui/material';
import AttendanceContext from './attendanceContext';
import PropTypes from 'prop-types';
import AmountCard from 'components/reusable/AmountCard';
import { GLOBAL_DATE_FORMAT, GLOBAL_DATE_TIME_FORMAT } from 'constant';
import { convertToLocal24, utcToLocal } from 'utils/time-conversion';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditPopover from './editDate';

const ReceptionTable = ({ parent }) => {
    const {
        handlePresent,
        attendanceData,
        attendanceLoading,
        attendanceFetching,
        pickUpData,
        pickUpLoading,
        pickUpFetching,
        page,
        setPage,
        showAttendance
    } = useContext(AttendanceContext);

    const isAttendance = parent === 'attendance';
    const tableData = isAttendance ? attendanceData : pickUpData;
    const isLoading = isAttendance ? attendanceLoading || attendanceFetching : pickUpLoading || pickUpFetching;

    const getButtonProps = (row) => {
        if (isAttendance) {
            if (row.present_status === 'Picked Up') {
                return {
                    color: 'error',
                    children: 'Picked up'
                };
            }
            return {
                color: row.present_status === 'Present' ? 'error' : 'success',
                children: row.present_status === 'Present' ? 'Mark Picked up' : 'Mark Present'
            };
        }
        return {
            color: 'error',
            children: 'checkout'
        };
    };

    const convertToLocalTime = (utcString, options = {}) => {
        const date = new Date(utcString);

        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true, // Change to false for 24-hour format
            ...options // Allow custom formatting options
        });
    };

    const [showEdit, setShowEdit] = useState(false);
    const [selectedStud, setSelectedStud] = useState(null);

    const handleEditStud = (field, row) => {
        setSelectedStud({ ...row, key: field, label: field === 'check_in_time' ? 'Present Time' : 'Picked Up Time' });
        setShowEdit(true);
    };
    const handleCloseEdit = () => {
        setShowEdit(false);
    };

    // const loading = true;
    // const [page, setPage] = useState(1);
    return (
        <>
            <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                <Grid item xs={3} sm={3} md={3} lg={3}>
                    <AmountCard
                        sx={{
                            height: 60
                        }}
                        loading={isLoading}
                        label="Total Count"
                        amount={tableData?.fields[0]?.total_count}
                        isAmount={false}
                    />
                </Grid>
                <Grid item xs={3} sm={3} md={3} lg={3}>
                    <AmountCard
                        sx={{
                            height: 60
                        }}
                        loading={isLoading}
                        label="Present Count"
                        amount={tableData?.fields[0]?.present_count}
                        isAmount={false}
                    />
                </Grid>
                <Grid item xs={3} sm={3} md={3} lg={3}>
                    <AmountCard
                        sx={{
                            height: 60
                        }}
                        loading={isLoading}
                        label="Absent Count"
                        amount={tableData?.fields[0]?.absent_count}
                        isAmount={false}
                    />
                </Grid>
                <Grid item xs={3} sm={3} md={3} lg={3}>
                    <AmountCard
                        sx={{
                            height: 60
                        }}
                        loading={isLoading}
                        label="Picked Up Count"
                        amount={tableData?.fields[0]?.pickup_count}
                        isAmount={false}
                    />
                </Grid>
            </Grid>
            {!tableData?.fields?.length ? (
                <Typography variant="h4" sx={{ mt: 5, display: 'flex', justifyContent: 'center' }}>
                    No Data Found
                </Typography>
            ) : (
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell width="250px" align="left">
                                    Name
                                </TableCell>
                                <TableCell width="200px" align="left">
                                    Classroom
                                </TableCell>
                                <TableCell width="200px" align="center">
                                    Status
                                </TableCell>
                                <TableCell width="250px" align="center">
                                    Present Time
                                </TableCell>
                                <TableCell width="250px" align="center">
                                    Picked Up Time
                                </TableCell>

                                <TableCell width="250px" align="center">
                                    Action
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell className="title-case" colSpan={6} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                showAttendance &&
                                tableData?.fields.map((row) => (
                                    <TableRow hover key={row?.student_id}>
                                        <TableCell width="250px" className="title-case" align="left">
                                            {row.student_name}
                                        </TableCell>
                                        <TableCell width="200px" className="title-case" align="left">
                                            {row.classroom_name}
                                        </TableCell>
                                        <TableCell width="200px" align="center">
                                            {row.present_status}
                                        </TableCell>
                                        <TableCell width="250px" align="center">
                                            {row.check_in_time ? utcToLocal(row.check_in_time, GLOBAL_DATE_TIME_FORMAT) : 'N/A'}
                                            {row.check_in_time ? (
                                                <Tooltip title="Edit Time">
                                                    <IconButton
                                                        title="Edit"
                                                        sx={{ paddingLeft: '12px' }}
                                                        onClick={() => handleEditStud('check_in_time', row)}
                                                    >
                                                        <EditNoteIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </TableCell>
                                        <TableCell width="250px" align="center">
                                            {row.check_out_time ? utcToLocal(row.check_out_time, GLOBAL_DATE_TIME_FORMAT) : 'N/A'}
                                            {row.check_out_time ? (
                                                <Tooltip title="Edit Time">
                                                    <IconButton
                                                        title="Edit"
                                                        sx={{ paddingLeft: '12px' }}
                                                        onClick={() => handleEditStud('check_out_time', row)}
                                                    >
                                                        <EditNoteIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </TableCell>
                                        <TableCell width="250px" align="center">
                                            <Button
                                                disabled={row.present_status === 'Picked Up'}
                                                key={row.id}
                                                loadingPosition="end"
                                                variant="outlined"
                                                {...getButtonProps(row)}
                                                onClick={() =>
                                                    handlePresent(
                                                        row.student_id,
                                                        row.classroom_location_id,
                                                        row.present_status !== 'Present',
                                                        parent
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <Pagination
                        color="primary"
                        sx={{
                            '& .MuiPagination-ul': {
                                flexWrap: 'nowrap',
                                justifyContent: 'flex-end'
                            },
                            marginTop: '25px',
                            marginBottom: '25px'
                        }}
                        showFirstButton
                        showLastButton
                        count={Math.ceil((tableData?.fields[0]?.total_count || 1) / (15 || 1))}
                        page={page}
                        onChange={(event, value) => setPage(value)}
                    />
                </TableContainer>
            )}
            <EditPopover isOpen={showEdit} data={selectedStud} handleClose={handleCloseEdit} parent={parent} />
        </>
    );
};

ReceptionTable.propTypes = {
    parent: PropTypes.string
};

export default ReceptionTable;
